import { Drawer } from "@JohnLewisPartnership/jl-design-system/dist/components/drawer/Drawer";
import {
  MessageWithCategoryAndType,
  groupMessagesByCategory,
} from "../../../../utils/product/getPromoMessaging.ts";
import styles from "./SpecialOffersDrawer.module.scss";
import SpecialOffersDrawerPromotion from "../SpecialOffersPromotion/SpecialOffersPromotion";
import { giftWithPurchaseTypes } from "../../../../utils/product/getPromoMessaging.ts";
import { Message } from "@/types/types";
import { SaleTagIcon } from "../../../__shared__/SaleTagIcon/SaleTagIcon";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

type SpecialOffersDrawerProps = {
  /**
   * An array of Message objects.
   */
  promotions: MessageWithCategoryAndType[];
  drawerIsOpen: boolean;
  onClose: () => void;
  onPromotionClose: () => void;
  isBlackOverride: boolean;
  prodCatEndpoint?: string;
  prodCatApiKey?: string;
  basketEndpoint?: string;
  specialOffersVariant?: string;
};

export const SpecialOffersDrawer = ({
  promotions,
  drawerIsOpen,
  onClose,
  onPromotionClose,
  isBlackOverride,
  prodCatEndpoint,
  prodCatApiKey,
  basketEndpoint,
  specialOffersVariant,
}: SpecialOffersDrawerProps) => {
  const { offers, services, claims } = groupMessagesByCategory(promotions);

  const freeGifts = offers.filter(
    (message: Message) =>
      message.promotionType && message.promotionType in giftWithPurchaseTypes,
  );
  const hasOneFreeGift = freeGifts.length === 1;

  const drawerStyles = cx(styles.versionThreeDrawerHeader, {
    redPromoDrawer: !isBlackOverride,
  });

  const drawerHeading =
    specialOffersVariant === "A" || specialOffersVariant === "B"
      ? promotions.length === 1
        ? "Offer"
        : "Offers"
      : promotions.length === 1
        ? "Special offer"
        : "Special offers";

  const offersAndServicesCount = offers.length + services.length;
  const claimsCount = claims.length;

  return (
    <Drawer
      isOpen={drawerIsOpen}
      onClose={onClose}
      heading={drawerHeading}
      className={drawerStyles}
    >
      {offersAndServicesCount > 0 && (
        <section className={styles.categorySection}>
          <div className={styles.categoryTitle}>
            {specialOffersVariant !== "A" && (
              <SaleTagIcon type="medium" isBlackOverride={isBlackOverride} />
            )}
            <h2
              className={cx(
                styles.title,
                isBlackOverride && styles["blackOverride"],
              )}
            >
              {offersAndServicesCount === 1
                ? "Offer available with this purchase"
                : "Offers available with this purchase"}
            </h2>
          </div>
          {offers.map((promotion, index) => (
            <SpecialOffersDrawerPromotion
              key={index}
              promotion={promotion}
              drawerIsOpen={drawerIsOpen}
              onClose={onPromotionClose}
              productHasOneFreeGift={hasOneFreeGift}
              prodCatEndpoint={prodCatEndpoint}
              prodCatApiKey={prodCatApiKey}
              basketEndpoint={basketEndpoint}
              specialOffersVariant={specialOffersVariant}
            />
          ))}
          {services.map((promotion, index) => (
            <SpecialOffersDrawerPromotion
              key={index}
              promotion={promotion}
              drawerIsOpen={drawerIsOpen}
              onClose={onPromotionClose}
              specialOffersVariant={specialOffersVariant}
            />
          ))}
        </section>
      )}
      {claimsCount > 0 && (
        <section className={styles.categorySection}>
          <div className={styles.categoryTitle}>
            {specialOffersVariant !== "A" && (
              <SaleTagIcon type="medium" isBlackOverride={isBlackOverride} />
            )}
            <h2
              className={cx(
                styles.title,
                isBlackOverride && styles["blackOverride"],
              )}
            >
              {claimsCount === 1
                ? "Claim this offer once you’ve made your purchase"
                : "Claim these offers once you’ve made your purchase"}
            </h2>
          </div>
          {claims.map((promotion, index) => (
            <SpecialOffersDrawerPromotion
              key={index}
              promotion={promotion}
              drawerIsOpen={drawerIsOpen}
              onClose={onPromotionClose}
              specialOffersVariant={specialOffersVariant}
            />
          ))}
        </section>
      )}
    </Drawer>
  );
};
